import {
    transformUserInstruments,
    transformVirtualIncentive,
} from '../../../api/transformations/userInstruments';
import { InstrumentType } from '../../../models/paymentMethods/enums';
import { initializeGooglePaySDKIfNeededAction } from '../../googlePay';
import {
    getUpdateUserInstrumentsAction,
    setVirtualIncentiveAction,
} from '../../user';
import { getAvailablePaymentMethods as getAvailablePaymentMethodsApi } from '../../../api/order';
import {
    isGooglePayAvailableAsPaymentMethod,
    isPaidOrder as isOrderToBePaid,
    isOrganizerManagingAnAlienOrder,
} from '../../../selectors/order';
import {
    getCanMakePaymentsWithActiveCard,
    isApplePayInCheckoutStepEnabled,
} from '../../../selectors/applePay';
import { initializeApplePay } from '../../applePay';
import { trackApplePayShowed } from '../../eventAnalytics';
import { gettext } from '@eventbrite/i18n';
import { CustomSubmissionError } from '@eventbrite/redux-form-tools';
import { change } from 'redux-form';
import {
    CHECKOUT_FORM_NAME,
    INSTRUMENT_TYPES,
    PAYMENT_METHODS,
} from '../../../constants';
import { isUserLoggedIn } from '../../../selectors/user';
import { isIdentificationRequiredForCurrency } from '../../../models/currency';
import { getCurrency } from '../../../selectors/event';
import { updateAvailablePaymentMethodsAction } from '../actions';
import { getVaultInstruments as getVaultInstrumentsApi } from '../../../api/users';
import { isEmpty } from 'lodash';

const _shouldManageUserSavedPaymentData = (state) =>
    isUserLoggedIn(state) && !isOrganizerManagingAnAlienOrder(state);

const _shouldSavePaymentMethods = (state) => {
    // [EB-112961] Currently we can't support vault instruments in currencies requiring identification (like BRL and ARG)
    const isVaultSupportedCurrency =
        !isIdentificationRequiredForCurrency[getCurrency(state)];

    return isVaultSupportedCurrency && _shouldManageUserSavedPaymentData(state);
};

export const getAndSaveAvailablePaymentMethods =
    () => async (dispatch, getState) => {
        const state = getState();
        const {
            user: { publicId: userId } = {},
            tickets: {
                constrainedInstrumentType = '',
                constrainedPaymentMethods = [],
            } = {},
            order: { id: orderId },
            event: { id: eventId },
        } = state;

        const isPaidOrder = isOrderToBePaid(state);

        if (!isPaidOrder) {
            return undefined;
        }

        const { response: availablePaymentData } =
            await getAvailablePaymentMethodsApi(eventId, orderId);

        let result = dispatch(
            updateAvailablePaymentMethodsAction(availablePaymentData),
        );

        if (isApplePayInCheckoutStepEnabled(getState())) {
            await dispatch(initializeApplePay());

            dispatch(
                trackApplePayShowed(
                    getCanMakePaymentsWithActiveCard(getState()),
                ),
            );
        }

        if (isGooglePayAvailableAsPaymentMethod(getState())) {
            await dispatch(initializeGooglePaySDKIfNeededAction());
        }

        const virtualIncentiveCardInstrument =
            availablePaymentData.user_instruments?.find(
                (userInstrument) =>
                    userInstrument.instrument?.instrument_type ===
                    InstrumentType.VIRTUAL_INCENTIVES_CARD,
            );

        if (virtualIncentiveCardInstrument) {
            const transformedVirtualIncentiveCardInstrument =
                transformVirtualIncentive(virtualIncentiveCardInstrument);

            if (transformedVirtualIncentiveCardInstrument) {
                dispatch(
                    setVirtualIncentiveAction(
                        transformedVirtualIncentiveCardInstrument,
                    ),
                );
            }
        }

        const hasOnlyOnePaymentMethodAndInstrumentType = (orderState) => {
            return orderState.availablePaymentMethods?.length === 1
                ? orderState.availablePaymentMethods[0].instrumentTypes
                      .length === 1
                : false;
        };

        //TODO EB-243615 Refactor to extract proper selectors

        const shouldSelectPaymentMethod =
            hasOnlyOnePaymentMethodAndInstrumentType(state.order);

        const haveAvailablePaymentMethods = (orderState) =>
            !!orderState.availablePaymentMethods;

        const hasNoPaymentMethodsAvailable = (orderState) =>
            orderState.availablePaymentMethods
                ? orderState.availablePaymentMethods.length === 0
                : true;

        if (
            haveAvailablePaymentMethods(state.order) &&
            hasNoPaymentMethodsAvailable(state.order)
        ) {
            throw new CustomSubmissionError(
                {
                    _error: gettext(
                        'This event is not available for purchase yet. Please contact the organizer in order to complete the purchase.',
                    ),
                },
                {},
            );
        }

        const getFirstAvailableInstrumentType = (orderState) =>
            orderState.availablePaymentMethods
                ? orderState.availablePaymentMethods[0].instrumentTypes[0]
                : null;

        if (shouldSelectPaymentMethod && isPaidOrder) {
            result = dispatch(
                change(
                    CHECKOUT_FORM_NAME,
                    'paymentMethod',
                    getFirstAvailableInstrumentType(state.order).type,
                ),
            );
        }

        // Fetch and save user's stored CC payment methods if user is logged in
        if (_shouldSavePaymentMethods(state)) {
            const { response: instrumentsResponse } =
                await getVaultInstrumentsApi(userId, new Date());

            const transformed = transformUserInstruments(instrumentsResponse);
            const eppMethods = getState().order.availablePaymentMethods.find(
                (method) => method.type === 'eventbrite',
            );

            if (eppMethods) {
                let allowedUserInstruments = transformed.userInstruments;
                const creditCardInstrument = eppMethods.instrumentTypes.find(
                    (instrument) => instrument.type === PAYMENT_METHODS.CREDIT,
                );

                // Filter out user instruments that do not fit in with the allowed CC variants.
                // ex) Don't show stored VISA card if the order can only use AMEX cards.
                if (creditCardInstrument) {
                    const allowedCards = creditCardInstrument.variants;

                    allowedUserInstruments = allowedUserInstruments
                        .filter((userInstrument) =>
                            allowedCards.includes(
                                userInstrument.instrument.cardType,
                            ),
                        )
                        .filter(
                            (userInstrument) =>
                                isEmpty(constrainedPaymentMethods) ||
                                constrainedPaymentMethods.includes(
                                    userInstrument.instrument.cardType,
                                ),
                        );
                }

                dispatch(
                    getUpdateUserInstrumentsAction(allowedUserInstruments),
                );

                if (
                    !isEmpty(allowedUserInstruments) ||
                    constrainedInstrumentType === INSTRUMENT_TYPES.CREDIT_CARD
                ) {
                    /**
                     * If there are stored payment methods or constrained instrument type is 'CREDIT_CARD',
                     * payment method selection is pre-set to CREDIT and the first stored payment method in
                     * the list is pre-selected. If there are no stored payment methods, fall back to 'CREDIT',
                     * which corresponds to the "add a new card" option.
                     */
                    const allowedVaultId = allowedUserInstruments.length
                        ? allowedUserInstruments[0].vaultId
                        : PAYMENT_METHODS.CREDIT;

                    dispatch(
                        change(
                            CHECKOUT_FORM_NAME,
                            'paymentMethod',
                            PAYMENT_METHODS.CREDIT,
                        ),
                    );
                    dispatch(
                        change(
                            CHECKOUT_FORM_NAME,
                            'payment.vaultId',
                            allowedVaultId,
                        ),
                    );
                } else {
                    return dispatch(
                        change(
                            CHECKOUT_FORM_NAME,
                            'payment.vaultId',
                            PAYMENT_METHODS.CREDIT,
                        ),
                    );
                }
            }
        }

        return result;
    };
